/**
 * Screen labels for the server's field and metric keys.
 *
 * The API speaks in stable identifiers; the console is read by people, so the
 * identifier is translated at the edge. Anything not listed falls back to the
 * key itself — visible, and a signal that a label is missing.
 */
const FIELD_LABELS: Record<string, string> = {
  from: '시작일',
  to: '종료일',
  storage_timezone: '저장 시간대',
  display_timezone: '표시 시간대',
  entries: '내역',
  range: '기간',
  collection: '수집 상태',
  watermark_at: '집계 기준 시각',
  last_run_at: '최근 집계',
  stale: '갱신 필요',
  series: '집계 내역',
  empty: '내역 없음',
  note: '참고',
  definition: '집계 기준',
  rolling_note: '기간 기준',
  cohorts: '유입 그룹',
  combined_total: '합계',
  combined_total_reason: '합계 제외 사유',
  canonical_origin: '서비스 주소',
  dev_fixtures_allowed: '개발용 예제 허용',
  mail_is_local_sink: '메일 테스트 수신함',
  client_budgets: '클라이언트 제한',
  limits: '사용 제한',
  configured: '연결 설정',
  implementation_state: '구현 상태',
  configuration_state: '설정 상태',
  verification_state: '실제 검증 상태',
  activation_state: '운영 활성 상태',
  // dashboard counts
  games_live: '서비스 중 게임',
  notices_draft: '작성 중 공지',
  approvals_pending: '대기 중 승인',
  mailings_scheduled: '예약된 발송',
  tickets_open: '열린 문의',
  incidents_open: '진행 중 장애',
  grants_today: '오늘 지급',
  outbox_pending: '전송 대기',
  outbox_dead: '전송 실패',

  email: '이메일',
  password: '비밀번호',
  translations: '본문 번역',
  body: '본문',
  expected_revision: '저장 버전',
  mode: '발급 방식',
  quantity: '발급 수량',
  reward_definition_id: '보상',

  // common columns
  metric: '지표',
  reason: '사유',
  environment: '환경',
  game_id: '게임',
  game_ids: '게임',
  state: '상태',
  status: '상태',
  kind: '종류',
  title: '제목',
  name: '이름',
  slug: '슬러그',
  created_at: '생성',
  updated_at: '수정',
  published_at: '발행',
  expires_at: '만료',
  starts_at: '시작',
  ends_at: '종료',
  revision: '리비전',
  version: '버전',
  total: '합계',
  count: '건수',
  amount: '수량',
  balance: '잔액',
  currency_code: '화폐',
  item_code: '아이템',
  platform: '플랫폼',
  provider: '제공자',
  locale: '언어',
  severity: '심각도',
  reference: '접수번호',
  subject_type: '대상 종류',
  subject_id: '대상',
  actor: '수행자',
  result: '결과',
};

const VALUE_LABELS: Record<string, string> = {
  game: '게임',
  notice: '공지',
  mailing: '선물·우편',
  ticket: '문의',
  production: '운영',
  development: '개발',
  staging: '검증',
  testing: '테스트',
  dau: '일일 이용자',
  mau: '월간 이용자',
  revenue: '매출',
  retention: '재방문',
  NOT_COLLECTED: '수집 안 함',
  NO_EXCHANGE_RATE_SOURCE: '환율 기준 없음',
  UNSUPPORTED: '검증 지원 없음',
  IMPLEMENTED: '구현됨',
  supported: '지원',
  no_data: '집계 내역 없음',
  ok: '정상',
  NO_IMPORTED_DATA: '가져온 데이터 없음',
  PENDING_MATURITY: '표본 부족',
  NOT_CONFIGURED: '미설정',
  NOT_VERIFIED: '미검증',
  no_recent_measurement: '최근 측정 없음',
};

export function fieldLabel(key: string): string {
  return FIELD_LABELS[key] ?? key;
}

export function valueLabel(value: unknown): string {
  if (typeof value !== 'string') return String(value ?? '—');

  return VALUE_LABELS[value] ?? value;
}

/** Localise validator copy without changing the API's field keys or response. */
export function validationCopy(value: string | string[]): string {
  return (Array.isArray(value) ? value : [value]).map((message) => {
    if (/[가-힣]/u.test(message)) return message;
    if (/field is required\./i.test(message) || message === 'required') return '필수 항목';
    if (/selected .+ is invalid\./i.test(message)) return '목록에서 항목 선택';
    if (/must be an integer\./i.test(message)) return '정수만 입력 가능';
    if (/must be a number\./i.test(message)) return '숫자만 입력 가능';
    if (/must be a string\./i.test(message)) return '텍스트 입력 필요';
    const minimum = /must be at least ([\d.]+)( characters)?\./i.exec(message);
    if (minimum) return `${minimum[1]}${minimum[2] ? '자' : ''} 이상 입력`;
    const maximum = /must not be greater than ([\d.]+)( characters)?\./i.exec(message);
    if (maximum) return `${maximum[1]}${maximum[2] ? '자' : ''} 이하로 입력`;
    return '입력값 확인 필요';
  }).join(', ');
}

export function validationFieldLabel(key: string): string {
  const [base, index, child] = key.split('.');
  const label = fieldLabel(base);
  const parent = label === base ? '입력 항목' : label;
  if (index && /^\d+$/.test(index)) {
    const childLabel = child ? fieldLabel(child) : '';
    return `${parent} ${Number(index) + 1}${childLabel && childLabel !== child ? ` · ${childLabel}` : ''}`;
  }
  return parent;
}
